import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AsyncPipe } from '@angular/common';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxMessageModule, NxMessageToastService } from '@aposin/ng-aquila/message';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxTaglistModule } from '@aposin/ng-aquila/taglist';
import { catchError, take, throwError } from 'rxjs';
import { Feature } from '../../../models/feature.model';
import { HttpError } from '../../../models/http-error.model';
import { FeatureService } from '../../../services/feature.service';
import { MockDataService } from '../../../services/mock-data.service';
import { PermissionService } from '../../../services/permission.service';
import {
  addEmailToDistributions as addEmailToDistributionsUtil,
  removeEmailFromDistributions as removeEmailFromDistributionsUtil,
  isValidEmail
} from '../../../utils/email-distribution.utils';
import { isLocalEnvironment } from '../../../utils/environment.utils';

@Component({
  selector: 'app-feature-form',
  standalone: true,
  imports: [
    NxFormfieldModule,
    NxTaglistModule,
    NxIconModule,
    NxInputModule,
    NxButtonModule,
    ReactiveFormsModule,
    NxSpinnerModule,
    NxMessageModule,
    AsyncPipe,
  ],
  templateUrl: './feature-form.component.html',
  styleUrl: './feature-form.component.scss'
})
export class FeatureFormComponent implements OnChanges, OnInit {
  private featureService = inject(FeatureService);
  private mockDataService = inject(MockDataService);
  private formBuilder = inject(FormBuilder);
  private messageToastService = inject(NxMessageToastService);
  permissionService = inject(PermissionService);

  @Input() pagerDutyServiceId: string | null = null;
  @Input() applicationId: string | null = null;
  @Input() featureId: string | null = null;
  @Output() closePanel = new EventEmitter<{ refreshList: boolean; updatedFeature?: Feature }>();

  isFeatureLoading = false;
  isFeatureUpdating = false;
  featureForm: FormGroup | null = null;
  emailDistributions: string[] = [];
  responseError: HttpError | null = null;

  get isCreateMode(): boolean {
    return !this.featureId;
  }

  ngOnChanges(): void {
    if (this.isCreateMode) {
      if (!this.featureForm) {
        this.initForm();
      }
      this.emailDistributions = [];
      this.featureForm?.patchValue({
        featureName: '',
        contactEmail: '',
        emailDistributions: []
      });
    } else {
      this.initData();
    }
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.featureForm = this.formBuilder.group({
      featureId: [{ value: null, disabled: true }],
      featureName: ['', Validators.required],
      contactEmail: ['', [Validators.required, Validators.email]],
      emailDistributions: [[]]
    });
  }

  addEmailToDistributions(value: string, inputElement: HTMLInputElement) {
    this.emailDistributions = addEmailToDistributionsUtil(value, inputElement, this.emailDistributions);
    this.featureForm?.patchValue({
      emailDistributions: this.emailDistributions
    });
  }

  removeEmailFromDistributions(email: string) {
    this.emailDistributions = removeEmailFromDistributionsUtil(email, this.emailDistributions);
    this.featureForm?.patchValue({
      emailDistributions: this.emailDistributions
    });
  }

  initData() {
    if (!this.pagerDutyServiceId || !this.applicationId || !this.featureId) {
      return;
    }

    this.responseError = null;
    this.emailDistributions = [];
    this.isFeatureLoading = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.getFeatureById(this.pagerDutyServiceId, this.applicationId, this.featureId)
      : this.featureService.getFeatureById(this.pagerDutyServiceId, this.applicationId, this.featureId);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to load feature', 'loadFeature', error)),
      )
      .subscribe((response) => {
        const feature = response?.body || null;
        this.isFeatureLoading = false;

        this.emailDistributions = feature?.emailDistributions ?? [];
        this.featureForm?.patchValue({
          featureId: feature?.featureId,
          featureName: feature?.featureName,
          contactEmail: feature?.contactEmail,
        });
      });
  }

  updateFeature(inputElement?: HTMLInputElement) {
    if (!this.pagerDutyServiceId || !this.applicationId || !this.featureForm || !this.featureForm.valid) {
      return;
    }

    if (inputElement && inputElement.value.trim()) {
      const trimmedEmail = inputElement.value.trim();

      if (isValidEmail(trimmedEmail)) {
        if (!this.emailDistributions.includes(trimmedEmail)) {
          this.emailDistributions = [...this.emailDistributions, trimmedEmail];
        }
      }

      inputElement.value = '';
    }

    this.isFeatureUpdating = true;
    this.responseError = null;

    if (this.isCreateMode) {
      this.createFeature();
    } else {
      this.updateExistingFeature();
    }
  }

  createFeature() {
    if (!this.pagerDutyServiceId || !this.applicationId || !this.featureForm) {
      return;
    }

    const formValue = this.featureForm.value;
    const createDto = {
      featureName: formValue.featureName,
      contactEmail: formValue.contactEmail,
      emailDistributions: this.emailDistributions || []
    };

    const dataService = isLocalEnvironment()
      ? this.mockDataService.createFeature(this.pagerDutyServiceId, this.applicationId, createDto)
      : this.featureService.createFeature(this.pagerDutyServiceId, this.applicationId, createDto);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to create feature', 'createFeature', error)),
      )
      .subscribe((response) => {
        this.isFeatureUpdating = false;
        this.messageToastService.open('Feature created successfully', {
          context: 'success',
          duration: 5000,
        });
        this.onCloseForm(true, response?.body || undefined);
      });
  }

  updateExistingFeature() {
    if (!this.pagerDutyServiceId || !this.applicationId || !this.featureId || !this.featureForm) {
      return;
    }

    const featureDto = {
      featureName: this.featureForm.value.featureName,
      contactEmail: this.featureForm.value.contactEmail,
      emailDistributions: this.emailDistributions
    };

    const dataService = isLocalEnvironment()
      ? this.mockDataService.updateFeature(this.pagerDutyServiceId, this.applicationId, this.featureId, featureDto)
      : this.featureService.updateFeature(this.pagerDutyServiceId, this.applicationId, this.featureId, featureDto);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to update feature', 'updateFeature', error)),
      )
      .subscribe((response) => {
        this.isFeatureUpdating = false;
        this.messageToastService.open('Feature updated successfully', {
          context: 'success',
          duration: 5000,
        });
        this.onCloseForm(true, response?.body || undefined);
      });
  }

  onCloseForm(refreshList = false, updatedFeature?: Feature) {
    this.featureForm?.reset();
    this.emailDistributions = [];
    this.closePanel.emit({ refreshList, updatedFeature });
  }

  handleError(error: string, errorType: string, errorDetails: HttpErrorResponse) {
    this.isFeatureLoading = false;
    this.isFeatureUpdating = false;

    this.responseError = {
      error,
      errorType,
      details: null
    };

    if (errorDetails?.error?.message) {
      this.responseError.details = errorDetails.error.message;
    }

    return throwError(() => error);
  }
}
